import { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

const WishlistContext = createContext();

export function WishlistProvider({ children }) {
  const { user } = useAuth();
  const [wishlistIds, setWishlistIds] = useState([]);

  const fetchWishlist = useCallback(async () => {
    if (!user) { setWishlistIds([]); return; }
    try {
      const resp = await api.get("/wishlist");
      setWishlistIds(resp.data.product_ids || []);
    } catch { setWishlistIds([]); }
  }, [user]);

  useEffect(() => { fetchWishlist(); }, [fetchWishlist]);

  const isInWishlist = useCallback((productId) => wishlistIds.includes(productId), [wishlistIds]);

  const toggleWishlist = useCallback(async (productId) => {
    if (!user) return;
    const inList = wishlistIds.includes(productId);
    setWishlistIds((prev) => inList ? prev.filter((id) => id !== productId) : [...prev, productId]);
    try {
      if (inList) {
        await api.delete(`/wishlist/${productId}`);
      } else {
        await api.post(`/wishlist/${productId}`);
        toast.success("Added to wishlist");
      }
    } catch {
      setWishlistIds((prev) => inList ? [...prev, productId] : prev.filter((id) => id !== productId));
    }
  }, [user, wishlistIds]);

  const value = useMemo(
    () => ({ wishlistIds, isInWishlist, toggleWishlist, fetchWishlist }),
    [wishlistIds, isInWishlist, toggleWishlist, fetchWishlist]
  );

  return (
    <WishlistContext.Provider value={value}>
      {children}
    </WishlistContext.Provider>
  );
}

export const useWishlist = () => useContext(WishlistContext);
